import { useState, useEffect } from "react";
import {
  Stack,
  Text,
  Container,
  SegmentedControl,
  NumberFormatter,
  Badge,
  Grid,
  Accordion,
  ScrollAreaAutosize,
  Divider,
  Paper,
  Group,
  Tooltip,
  Tabs,
} from "@mantine/core";
import {
  IconListDetails,
  IconHierarchy2,
  IconTimeline,
  IconAlertTriangle,
} from "@tabler/icons-react";
import TagGroup from "../_Common/TagGroup";
import useIsMobile from "../_Common/useIsMobile";
import InferenceRequest from "./InferenceRequest";
import InferenceResponse from "./InferenceResponse";
import StepHeader from "./IterationHeader";
import { ToolCallStepPanel } from "./ToolCallDetails";
import { warningLabel } from "./StatusBadge";
import { RailRow, RailSection } from "./RailSection";
import LogEntryViewer from "./LogEntryViewer";
import RequestHierarchyPanel from "./RequestHierarchyPanel";
import AgentExecutionGraph from "./AgentExecutionGraph";
import ExecutionTimeline from "./ExecutionTimeline";
import FeedbackPanel from "./FeedbackPanel";
import { describeReasoningEffort } from "../_Common/reasoningEffort";
import { getAgentTreeForLog, getToolEventsForRequest } from "../api";
import { useTools } from "../Tools/toolStore";
import { useStore } from "../store";
import { useLocation } from "wouter";

/**
 * The degradations the run recorded while still finishing. Deliberately its own panel rather
 * than a line under the status: the whole point is that these were previously invisible, and a
 * run whose tools all returned 502 should not need a step-by-step read to notice.
 */
function RunHealthPanel({ log }) {
  const warnings = log.warnings || [];
  const count = log.warningCount || warnings.length;
  if (!count) return null;

  // Collapse repeats: N failures of the same tool are one line with a multiplier.
  const grouped = [];
  for (const w of warnings) {
    const key = `${w.code}|${w.subject || ""}`;
    const existing = grouped.find((g) => g.key === key);
    if (existing) existing.n += 1;
    else grouped.push({ key, n: 1, ...w });
  }
  const dropped = count - warnings.length;

  return (
    <Paper
      withBorder
      p="sm"
      radius="sm"
      style={{ borderColor: "var(--mantine-color-yellow-6)" }}
    >
      <Group gap="xs" mb="xs">
        <IconAlertTriangle size={14} color="var(--mantine-color-yellow-6)" />
        <Text size="xs" fw={700} tt="uppercase" c="yellow.6">
          Run health · {count} {count === 1 ? "warning" : "warnings"}
        </Text>
      </Group>
      <Stack gap={6}>
        {grouped.map((w) => (
          <div key={w.key}>
            <Group gap={6} wrap="nowrap">
              <Badge size="xs" color="yellow" variant="light">
                {warningLabel(w.code)}
                {w.n > 1 ? ` ×${w.n}` : ""}
              </Badge>
              {w.subject && (
                <Text size="xs" c="dimmed" truncate>
                  {w.subject}
                </Text>
              )}
            </Group>
            <Text size="xs" c="dimmed" mt={2}>
              {w.message}
            </Text>
          </div>
        ))}
        {dropped > 0 && (
          <Text size="xs" c="dimmed" fs="italic">
            +{dropped} older warning{dropped === 1 ? "" : "s"} not shown (only
            the most recent 25 are kept).
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Why a failed run failed. The backend stores a sanitized reason (never the raw exception) on
 * the run document; older failed runs predate the field and still show only the red badge.
 */
function FailureReasonPanel({ log }) {
  // Wire values are camelCased C# enum names, so compare lower-cased (CLAUDE.md).
  if (log.status?.toLowerCase() !== "failed" || !log.failureReason) return null;

  return (
    <Paper
      withBorder
      p="sm"
      radius="sm"
      style={{ borderColor: "var(--mantine-color-red-6)" }}
    >
      <Group gap="xs" mb="xs">
        <IconAlertTriangle size={14} color="var(--mantine-color-red-6)" />
        <Text size="xs" fw={700} tt="uppercase" c="red.6">
          Failure reason
        </Text>
      </Group>
      <Text size="sm">{log.failureReason}</Text>
    </Paper>
  );
}

function formatProbability(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toFixed(2);
}

/**
 * A decision run has no chat steps. Show the question, the kind, and the answer.
 * Image bytes are never stored. Evidence text is absent when the run missed the sample.
 */
function DecisionLogView({ log }) {
  const decision = log.decision || {};
  const questions = decision.questions || [];
  const answers = decision.answers || [];
  const answerFor = (question, index) =>
    answers.find(
      (answer) =>
        question.name &&
        answer.name &&
        answer.name.toLowerCase() === question.name.toLowerCase()
    ) || answers[index];

  return (
    <Container fluid p={0}>
      <Stack gap="md">
        <FailureReasonPanel log={log} />
        {log.detailDropped && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="sm">
              The evidence text was not stored for this run. It missed the sample
              rate (the tenant, environment or flow rate, whichever is highest).
              The answers are still here.
            </Text>
          </Paper>
        )}
        <Group gap="xs">
          <Badge variant="light" color="cyan">
            Decision
          </Badge>
          {decision.provider && (
            <Badge variant="light" color="grape">
              {decision.provider}
            </Badge>
          )}
          <Text size="sm">{decision.servedModel || log.model}</Text>
          {decision.requestedModel &&
            decision.servedModel &&
            decision.requestedModel !== decision.servedModel && (
              <Text size="sm" c="dimmed">
                requested {decision.requestedModel}
              </Text>
            )}
        </Group>
        {decision.inputText && (
          <Paper withBorder p="sm" radius="sm">
            <Text size="xs" c="dimmed" mb={4}>
              Evidence
            </Text>
            <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
              {decision.inputText}
            </Text>
          </Paper>
        )}
        {decision.hasImage && (
          <Text size="sm" c="dimmed">
            This decision included an image. The image is not stored.
          </Text>
        )}
        <Stack gap="sm">
          {questions.map((question, index) => {
            const answer = answerFor(question, index);
            const kind = (answer?.kind || question.kind || "").toString().toLowerCase();
            return (
              <Paper withBorder p="sm" radius="sm" key={`${question.name || "q"}-${index}`}>
                <Group gap="xs" mb={6}>
                  <Text size="sm" fw={600}>
                    {question.name || `Question ${index + 1}`}
                  </Text>
                  <Badge size="xs" variant="light" tt="none">
                    {kind || "decision"}
                  </Badge>
                </Group>
                {question.instructions && (
                  <Text size="sm" c="dimmed" mb={6}>
                    {question.instructions}
                  </Text>
                )}
                <DecisionAnswerLine kind={kind} answer={answer} />
              </Paper>
            );
          })}
          {questions.length === 0 && (
            <Text size="sm" c="dimmed">
              This run has no stored questions.
            </Text>
          )}
        </Stack>
      </Stack>
    </Container>
  );
}

function DecisionAnswerLine({ kind, answer }) {
  if (!answer) return <Text size="sm">No answer was stored.</Text>;
  if (kind === "refusal") return <Text size="sm">Refused.</Text>;
  if (kind === "predicate") {
    return (
      <Text size="sm">
        Probability {formatProbability(answer.probability)}
      </Text>
    );
  }
  if (kind === "choice") {
    const choice = answer.choiceIsBoolean
      ? `boolean ${answer.choiceText}`
      : answer.choiceText;
    return (
      <Stack gap={4}>
        <Text size="sm">
          {choice || "—"}
          {answer.confidence != null &&
            ` · confidence ${formatProbability(answer.confidence)}`}
        </Text>
        <DecisionProbabilityList items={answer.probabilities} />
      </Stack>
    );
  }
  if (kind === "score") {
    return (
      <Stack gap={4}>
        <Text size="sm">
          Score {formatProbability(answer.score)}
          {answer.confidence != null &&
            ` · confidence ${formatProbability(answer.confidence)}`}
        </Text>
        <DecisionProbabilityList items={answer.probabilities} />
      </Stack>
    );
  }
  return <Text size="sm">No answer was stored.</Text>;
}

function DecisionProbabilityList({ items }) {
  if (!items?.length) return null;
  return (
    <Text size="xs" c="dimmed">
      {items
        .map((item) => {
          const label = item.valueIsBoolean
            ? `boolean ${item.text}`
            : item.label || item.text || item.index;
          return `${label} ${formatProbability(item.probability)}`;
        })
        .join(" · ")}
    </Text>
  );
}

function RequestConfigPanel({ log }) {
  const firstRequest = log.requests[0];
  const { tools } = useTools();
  const providers = useStore((state) => state.providers);

  if (!firstRequest) return null;

  const hasTools = firstRequest.toolIds?.length > 0;
  const hasSchema = !!firstRequest.responseSchema;
  // Clamping is a property of the model that actually answered, which is not necessarily the one
  // the request named — a fallback candidate can have a different ceiling.
  const servedModel =
    log.responses?.find(
      (r) => r.inferenceRequestId === firstRequest.id && r.status !== "failure"
    )?.model || firstRequest.model;
  const effort = describeReasoningEffort(
    firstRequest.reasoningEffort,
    servedModel,
    providers
  );

  const effortDegraded =
    effort?.state === "clamped" ||
    effort?.state === "raised" ||
    effort?.state === "ignored";

  return (
    <RailSection label="Request config">
      <Stack gap={6}>
        <RailRow label="Model">
          <Group gap={6}>
            <Badge size="sm" color="grape" variant="light" tt="none" radius="sm">
              {firstRequest.model}
            </Badge>
            {log.overrideModel && (
              <Tooltip label="Caller-specified override model" withArrow>
                <Badge
                  size="sm"
                  color="orange"
                  variant="light"
                  tt="none"
                  radius="sm"
                >
                  override: {log.overrideModel}
                </Badge>
              </Tooltip>
            )}
          </Group>
        </RailRow>
        {effort && (
          <RailRow label="Reasoning">
            <Tooltip label={effort.note} multiline w={300} position="left" withArrow>
              <Text
                fz="sm"
                c={effortDegraded ? "yellow.6" : undefined}
                style={{ cursor: "help" }}
              >
                {effort.label}
                {(effort.state === "clamped" || effort.state === "raised") &&
                  ` → ${effort.effectiveLabel}`}
                {effort.state === "ignored" && " (ignored)"}
              </Text>
            </Tooltip>
          </RailRow>
        )}
        {firstRequest.temperature != null && (
          <RailRow label="Temperature">{firstRequest.temperature}</RailRow>
        )}
        {firstRequest.maxToolCalls > 0 && (
          <RailRow label="Max calls">{firstRequest.maxToolCalls}</RailRow>
        )}
        {hasSchema && (
          <RailRow label="Output">
            <Badge size="sm" color="indigo" variant="light" tt="none" radius="sm">
              Response schema
            </Badge>
          </RailRow>
        )}
        {hasTools && (
          <RailRow label="Tools" align="top">
            <Group gap={4}>
              {firstRequest.toolIds.map((id) => {
                const tool = tools.find((t) => t.id === id);
                return (
                  <Badge
                    key={id}
                    size="sm"
                    radius="sm"
                    variant="default"
                    tt="none"
                  >
                    {tool?.name || id}
                  </Badge>
                );
              })}
            </Group>
          </RailRow>
        )}
      </Stack>
    </RailSection>
  );
}

export default function InvocationLogDetails({ log, onNavigateToRequest }) {
  const [displayMode, setDisplayMode] = useState("mono");
  const profile = useStore((state) => state.profile);
  const [, setLocation] = useLocation();
  const isMobile = useIsMobile();

  // Fetch agent tree for timeline (shared with graph tab)
  const [agentTree, setAgentTree] = useState(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState(null);

  const isAgentRequest = log?.agentRole || log?.agentDepth > 0 || log?.flowName;
  const hasChildAgents = log?.childAgentCreditsUsed > 0;

  useEffect(() => {
    setAgentTree(null);
    setTreeError(null);
    if (!log?.id || !isAgentRequest || !hasChildAgents) return;
    setTreeLoading(true);
    getAgentTreeForLog(log.id)
      .then(setAgentTree)
      .catch((err) => setTreeError(err.message || "Failed to load agent tree"))
      .finally(() => setTreeLoading(false));
  }, [log?.id, isAgentRequest, hasChildAgents]);

  // Fetch tool events when any iteration has tool calls
  const [toolEvents, setToolEvents] = useState(null);
  const hasToolCalls = log?.stats?.totalFunctionCalls > 0;

  useEffect(() => {
    setToolEvents(null);
    if (!log?.id || !hasToolCalls) return;
    getToolEventsForRequest(log.id)
      .then(setToolEvents)
      .catch(() => setToolEvents(null));
  }, [log?.id, hasToolCalls]);

  const handleNavigate = (requestId) => {
    if (onNavigateToRequest) {
      onNavigateToRequest(requestId);
    } else {
      setLocation(`/logs/${requestId}`);
    }
  };

  if (!log) return null;

  if ((log.type || "").toString().toLowerCase() === "decision" || log.decision) {
    return <DecisionLogView log={log} />;
  }

  const hasTags = log.tags?.length > 0;
  const hasReferenceIds = log.referenceIds?.length > 0;

  // --- Build iterations (internal data grouping) ---
  // Pair each request with its matching response(s) by ID.
  const iterations = log.requests.map((request, idx) => {
    const matchingResponses = log.responses
      .filter((r) => r.inferenceRequestId === request.id)
      .sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0));
    const response =
      matchingResponses.find((r) => r.status !== "failure") ||
      matchingResponses[matchingResponses.length - 1];
    const failedProviderAttempts = matchingResponses.filter(
      (r) => r !== response && r.status === "failure"
    );
    const prevMessageCount =
      idx > 0 ? log.requests[idx - 1].messages?.length || 0 : 0;
    const rawDelta = request.messages?.slice(prevMessageCount) || [];
    // Filter out AI messages that only contain FunctionCall parts (echoes of
    // previous response) and messages that only contain FunctionResult parts
    // (tool results shown in their own steps).
    const deltaMessages = rawDelta.filter(
      (m) =>
        !(
          m.role === "ai" &&
          m.content?.length > 0 &&
          m.content.every((c) => c.type === "functionCall")
        ) &&
        !(
          m.content?.length > 0 &&
          m.content.every((c) => c.type === "functionResult")
        )
    );
    return {
      request,
      response,
      failedProviderAttempts,
      deltaMessages,
      prevMessageCount,
    };
  });

  // --- Index tool events by callId ---
  const startedByCallId = {};
  const completedByCallId = {};
  if (toolEvents) {
    for (const event of toolEvents) {
      const callId = event.data?.callId;
      if (!callId) continue;
      // The API serializes ShuttleEventType as camelCase ("toolStarted"/"toolCompleted"/"toolFailed");
      // compare case-insensitively so PascalCase values match too (otherwise every tool call renders
      // "No timing data" even though the events exist).
      const type = (event.type || "").toLowerCase();
      if (type === "toolstarted") {
        startedByCallId[callId] = event;
      } else if (type === "toolcompleted" || type === "toolfailed") {
        completedByCallId[callId] = event;
      }
    }
  }

  // --- Index tool results by callId, across the WHOLE conversation ---
  // Deliberately not per-iteration-delta: call ids are unique within a run, so scanning every
  // message finds a result regardless of which iteration's slice it landed in. The delta
  // arithmetic depends on each request row reporting a message count, and a row that can't
  // report one (unresolvable message sequence, stripped content) silently emptied the following
  // step's delta — after which every tool call in it fell back to the 500-char event preview and
  // looked like it had no result at all.
  const resultByCallId = {};
  for (const message of log.messages || []) {
    for (const part of message.content || []) {
      const callId = part?.toolCallResult?.callId;
      if (part?.type === "functionResult" && callId) resultByCallId[callId] = part;
    }
  }

  // --- Build linear steps: Inference → Tool Call → Tool Call → Inference → ... ---
  const steps = [];
  let stepNumber = 1;
  for (let i = 0; i < iterations.length; i++) {
    const iteration = iterations[i];

    // Inference step
    steps.push({
      type: "inference",
      stepNumber: stepNumber++,
      key: `inf-${iteration.request.id}`,
      request: iteration.request,
      response: iteration.response,
      failedProviderAttempts: iteration.failedProviderAttempts,
      deltaMessages: iteration.deltaMessages,
      prevMessageCount: iteration.prevMessageCount,
    });

    // Tool call steps (one per tool call in the response)
    if (iteration.response?.toolCalls?.length > 0) {
      for (const tc of iteration.response.toolCalls) {
        const num = stepNumber++;
        const tcCompleted = completedByCallId[tc.refId];
        const tcResult = resultByCallId[tc.refId];

        // Whether the call failed is decided by the backend and carried on the event — the
        // only source that is always present. Reading it out of the result payload was the
        // bug: results over 1 KB are swapped for a blob reference, so exactly the failures
        // most likely to be large (an HTML error page from a proxy) arrived with no content
        // to inspect and rendered as a green "OK".
        const isTcFailed =
          tcCompleted?.data?.status === "failed" ||
          tcCompleted?.type?.toLowerCase() === "toolfailed";
        const resultContent = tcResult?.toolCallResult?.content;
        // Legacy fallback for runs recorded before the backend flagged failures: the old shape
        // put the status inside the result, and older previews still carry it as text.
        const legacyErrorCode =
          (typeof resultContent === "object" && resultContent?.errorCode) ||
          (tcResult?.toolCallResult?.isError &&
            (tcResult.toolCallResult.errorKind || "error")) ||
          (!tcResult &&
            /"errorCode"\s*:\s*"([^"]+)"/.exec(
              tcCompleted?.data?.resultPreview || ""
            )?.[1]) ||
          null;
        const httpErrorCode =
          tcCompleted?.data?.httpStatus ||
          tcResult?.toolCallResult?.httpStatusCode ||
          legacyErrorCode;
        const hasError = isTcFailed || !!httpErrorCode;

        steps.push({
          type: "toolCall",
          stepNumber: num,
          key: `tc-${tc.refId || num}`,
          toolCall: tc,
          startedEvent: startedByCallId[tc.refId],
          completedEvent: tcCompleted,
          functionResult: tcResult,
          hasError,
          httpErrorCode: httpErrorCode || null,
          errorKind:
            tcCompleted?.data?.errorKind ||
            tcResult?.toolCallResult?.errorKind ||
            null,
        });
      }
    }
  }

  // Annotate inference steps with downstream tool call error counts
  for (let i = 0; i < steps.length; i++) {
    if (steps[i].type === "inference") {
      let errorCount = 0;
      for (
        let j = i + 1;
        j < steps.length && steps[j].type === "toolCall";
        j++
      ) {
        if (steps[j].hasError) errorCount++;
      }
      steps[i].toolCallErrors = errorCount;
    }
  }

  // Default open the last inference step that has a text response
  const defaultStepValue =
    steps
      .slice()
      .reverse()
      .find(
        (s) =>
          s.type === "inference" &&
          (s.response?.textResponse || s.response?.verbatimResponse)
      )?.key || steps[0]?.key;

  // Content for the Details tab (requests/responses)
  const stepsColumn = (
    <>
      {log.detailDropped && (
        <Paper withBorder p="sm" radius="sm" mb="sm">
          <Text size="sm">
            Full prompt and answer text was not stored for this run — it missed
            the sample rate (the tenant, environment or flow rate, whichever is
            highest). Cost, model, status and timing are still here. Debug runs
            always keep full text.
          </Text>
        </Paper>
      )}
      {/* The rendering mode acts on this column, so it lives here rather than docked among
          the data panels in the rail, where it read as one more fact about the run. */}
      <Group
        justify="space-between"
        align="center"
        mb="xs"
        wrap={isMobile ? "wrap" : "nowrap"}
      >
        <Text fw={500}>
          {steps.length} {steps.length === 1 ? "Step" : "Steps"}
        </Text>
        <Tooltip
          label="How message text is rendered in requests and responses"
          position="left"
          withArrow
        >
          <SegmentedControl
            value={displayMode}
            size="xs"
            onChange={setDisplayMode}
            data={[
              { label: "Mono", value: "mono" },
              { label: "Markdown", value: "markdown" },
              { label: "Raw", value: "raw" },
            ]}
          />
        </Tooltip>
      </Group>
      <Accordion
        variant="contained"
        // The label is a flex child of the control; without an explicit `minWidth: 0` it
        // refuses to shrink below its content, and a step header full of badges drags the
        // whole panel past the viewport instead of truncating inside it.
        styles={{
          content: { padding: 0, overflow: "hidden" },
          label: { minWidth: 0, overflow: "hidden" },
        }}
        defaultValue={defaultStepValue}
      >
        {steps.map((step) => (
          <Accordion.Item key={step.key} value={step.key}>
            <Accordion.Control>
              <StepHeader
                step={step}
                compact={isMobile}
                mr={isMobile ? "xs" : "xl"}
              />
            </Accordion.Control>
            <Accordion.Panel>
              {step.type === "inference" ? (
                <Stack gap={0}>
                  <InferenceRequest
                    displayMode={displayMode}
                    request={step.request}
                    messages={step.deltaMessages}
                    messageOffset={step.prevMessageCount}
                  />
                  {step.failedProviderAttempts?.length > 0 && (
                    <Divider
                      my="xs"
                      label={`${step.failedProviderAttempts.length} failed provider ${step.failedProviderAttempts.length === 1 ? "attempt" : "attempts"}`}
                      labelPosition="center"
                      color="red"
                    />
                  )}
                  {step.failedProviderAttempts?.map((failedResp) => (
                    <Paper
                      key={failedResp.id}
                      withBorder
                      p="xs"
                      mx="sm"
                      mb="xs"
                      radius="sm"
                      style={{
                        borderColor: "var(--mantine-color-red-4)",
                        opacity: 0.75,
                      }}
                    >
                      <Group gap="xs" mb={4}>
                        <Badge size="xs" color="red" variant="light">
                          Failed
                        </Badge>
                        {failedResp.provider && (
                          <Badge size="xs" color="grape" variant="light">
                            {failedResp.provider}
                          </Badge>
                        )}
                        {failedResp.isRetryable && (
                          <Badge size="xs" color="yellow" variant="light">
                            Retryable
                          </Badge>
                        )}
                        {failedResp.milliseconds > 0 && (
                          <Text size="xs" c="dimmed">
                            {failedResp.milliseconds}ms
                          </Text>
                        )}
                      </Group>
                      {failedResp.verbatimResponse && (
                        <Text
                          size="xs"
                          c="red"
                          ff="monospace"
                          lineClamp={3}
                        >
                          {failedResp.verbatimResponse}
                        </Text>
                      )}
                    </Paper>
                  ))}
                  {step.response && (
                    <>
                      <Divider
                        my="xs"
                        label="Response"
                        labelPosition="center"
                      />
                      <InferenceResponse
                        displayMode={displayMode}
                        response={step.response}
                      />
                    </>
                  )}
                </Stack>
              ) : (
                <ToolCallStepPanel step={step} />
              )}
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>

      {profile.userRole == "GlobalAdmin" && (
        <>
          <Text mt="md">Log Entries</Text>
          <LogEntryViewer requestId={log.id} />
        </>
      )}
    </>
  );

  // Ordered by how much the section can change your reading of the run: what it cost, what it
  // was configured to do, where it sits in the tree, and only then the identifiers. Run health
  // is the one section allowed to shout, so it sits outside this group — on a narrow screen it
  // has to lead, and the rest of the rail moves below the steps.
  const railSections = (
    <Stack gap="xl">
      <RailSection label="Timing & cost">
        <Stack gap={6}>
          <RailRow label="Runtime">
            <NumberFormatter
              thousandSeparator
              value={log.milliseconds}
              suffix=" ms"
            />
          </RailRow>
          <RailRow label="Total cost">
            <NumberFormatter
              suffix="c"
              decimalScale={4}
              value={log.totalTreeCredits / 10_000}
            />
          </RailRow>
          {log.childAgentCreditsUsed > 0 && (
            <>
              <RailRow label="Direct">
                <Text fz="sm" c="dimmed">
                  <NumberFormatter
                    suffix="c"
                    decimalScale={4}
                    value={log.creditsUsed / 10_000}
                  />
                </Text>
              </RailRow>
              <RailRow label="Sub-agents">
                <Text fz="sm" c="dimmed">
                  <NumberFormatter
                    suffix="c"
                    decimalScale={4}
                    value={log.childAgentCreditsUsed / 10_000}
                  />
                </Text>
              </RailRow>
            </>
          )}
        </Stack>
      </RailSection>

      <RequestConfigPanel log={log} />

      <RequestHierarchyPanel
        requestId={log.id}
        onNavigate={handleNavigate}
      />

      <FeedbackPanel requestId={log.id} />

      {hasTags && (
        <RailSection label="Tags">
          <TagGroup size="xs" tags={log.tags} />
        </RailSection>
      )}

      {hasReferenceIds && (
        <RailSection label="Reference IDs">
          <TagGroup size="xs" tags={log.referenceIds} color="cyan" />
        </RailSection>
      )}

      {/* Environment, debug and the request id moved into the modal header — burying the
          environment in a disclosure was hiding the most consequential fact down here. What
          is left are identifiers you copy, so they render plainly rather than behind a
          toggle that opened onto two rows. */}
      {(log.contextId || log.endCustomerExternalId || log.endCustomerId) && (
        <RailSection label="Metadata">
          <Stack gap={6}>
            {log.contextId && (
              <RailRow label="Context ID">
                <Text fz="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                  {log.contextId}
                </Text>
              </RailRow>
            )}
            {(log.endCustomerExternalId || log.endCustomerId) && (
              <RailRow label="Customer">
                <Text fz="xs" ff="monospace" style={{ wordBreak: "break-all" }}>
                  {log.endCustomerExternalId || log.endCustomerId}
                </Text>
              </RailRow>
            )}
          </Stack>
        </RailSection>
      )}
    </Stack>
  );

  // Two columns side by side is a desktop shape: at 390px the 3-of-12 rail is ~90px wide, which
  // is narrower than the badges it holds. On mobile the run stacks in reading order instead —
  // what went wrong, then what happened, then the reference data folded away.
  const detailsContent = isMobile ? (
    <Stack gap="md">
      <FailureReasonPanel log={log} />
      <RunHealthPanel log={log} />
      <div style={{ minWidth: 0 }}>{stepsColumn}</div>
      <Accordion variant="contained">
        <Accordion.Item value="run-info">
          <Accordion.Control>
            <Text fz="sm" fw={500}>
              Run details
            </Text>
          </Accordion.Control>
          <Accordion.Panel>{railSections}</Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  ) : (
    <Grid>
      <Grid.Col span={9} style={{ overflow: "hidden", minWidth: 0 }}>
        {/* The modal scrolls the page; on desktop the step list gets its own viewport so the
            rail stays put beside it. On mobile that would be a scroll region inside a scroll
            region, so the mobile branch above leaves it off. */}
        <ScrollAreaAutosize mah="90vh">{stepsColumn}</ScrollAreaAutosize>
      </Grid.Col>
      <Grid.Col span={3} style={{ overflow: "hidden", minWidth: 0 }}>
        <Stack gap="xl">
          <FailureReasonPanel log={log} />
          <RunHealthPanel log={log} />
          {railSections}
        </Stack>
      </Grid.Col>
    </Grid>
  );

  // If this is an agent request with child agents, show tabs
  if (isAgentRequest && hasChildAgents) {
    return (
      <Container fluid p={0}>
        <Tabs defaultValue="details">
          {/* Three tabs with icons overflow a phone; let the strip scroll rather than let it
              set the width of everything below it. */}
          <Tabs.List
            mb="md"
            style={{ flexWrap: "nowrap", overflowX: "auto" }}
          >
            <Tabs.Tab
              value="details"
              leftSection={<IconListDetails size={14} />}
            >
              Details
            </Tabs.Tab>
            <Tabs.Tab
              value="timeline"
              leftSection={<IconTimeline size={14} />}
            >
              Timeline
            </Tabs.Tab>
            <Tabs.Tab
              value="graph"
              leftSection={<IconHierarchy2 size={14} />}
            >
              {isMobile ? "Graph" : "Execution Graph"}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="details">{detailsContent}</Tabs.Panel>

          <Tabs.Panel value="timeline">
            <ExecutionTimeline
              tree={agentTree}
              loading={treeLoading}
              error={treeError}
            />
          </Tabs.Panel>

          <Tabs.Panel value="graph">
            <AgentExecutionGraph requestId={log.id} />
          </Tabs.Panel>
        </Tabs>
      </Container>
    );
  }

  // For non-agent requests or agents without children, just show details
  return (
    <Container fluid p={0}>
      <Stack justify="space-between" gap="lg">
        {detailsContent}
      </Stack>
    </Container>
  );
}
